/* eslint-disable react-refresh/only-export-components */
/**
 * Toast Context
 *
 * Global toast notification management using React Context.
 * Supports success, error, and info toasts with auto-dismiss.
 *
 * CRITICAL CONSTRAINTS:
 * - NO localStorage/sessionStorage (React state only)
 * - TypeScript strict mode (NO 'any' types)
 * - Success toasts auto-dismiss (3-5 seconds)
 * - Error toasts can be persistent or auto-dismiss
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

export type ToastType = 'success' | 'error' | 'info';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration: number;
  persistent?: boolean;
}

export interface ToastContextType {
  toasts: Toast[];
  showToast: (message: string, type: ToastType, duration?: number, persistent?: boolean) => void;
  dismissToast: (id: string) => void;
}

// Create ToastContext with undefined default
const ToastContext = createContext<ToastContextType | undefined>(undefined);

/**
 * Custom hook to access ToastContext
 * @throws Error if used outside ToastProvider
 */
export function useToast(): ToastContextType {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}

interface ToastProviderProps {
  children: React.ReactNode;
}

/**
 * ToastProvider Component
 *
 * Wraps app to provide global toast notification management.
 */
export function ToastProvider({ children }: ToastProviderProps): React.JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);

  /**
   * Dismiss a toast by ID
   *
   * @param id - Toast ID to dismiss
   */
  const dismissToast = useCallback((id: string): void => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  /**
   * Show a toast notification
   *
   * @param message - Toast message text
   * @param type - Toast type (success, error, info)
   * @param duration - Auto-dismiss duration in ms (default: 3000 for success, 5000 for error/info)
   */
  const showToast = useCallback(
    (message: string, type: ToastType, duration?: number, persistent?: boolean): void => {
      // Default durations based on type
      const defaultDuration = type === 'success' ? 3000 : 5000;
      const toastDuration = duration ?? defaultDuration;

      // Generate unique ID
      const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Create toast object
      const newToast: Toast = {
        id,
        message,
        type,
        duration: toastDuration,
        persistent,
      };

      // Add toast to state
      setToasts((prev) => [...prev, newToast]);

      // Auto-dismiss after duration (only if not persistent)
      if (!persistent) {
        setTimeout(() => {
          dismissToast(id);
        }, toastDuration);
      }
    },
    [dismissToast]
  );

  const value: ToastContextType = {
    toasts,
    showToast,
    dismissToast,
  };

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}
